import type { PlatformDashboardCapacityMetric, PlatformDashboardSummary } from '@livechat/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getPlatformDashboard } from '../api.js';

export const DASHBOARD_REFRESH_MS = 5_000;

export function formatBytes(bytes: number): string {
	if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
	const units = ['B', 'KB', 'MB', 'GB', 'TB'];
	const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	const value = bytes / 1024 ** unit;
	return `${value >= 10 || unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

export function formatDuration(seconds: number): string {
	const safeSeconds = Math.max(0, Math.floor(seconds));
	const days = Math.floor(safeSeconds / 86_400);
	const hours = Math.floor((safeSeconds % 86_400) / 3_600);
	const minutes = Math.floor((safeSeconds % 3_600) / 60);
	return days ? `${days}d ${hours}h` : hours ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function CapacityCard({
	label,
	metric,
}: {
	label: string;
	metric: PlatformDashboardCapacityMetric;
}) {
	return (
		<article className="owner-meter-card">
			<div className="owner-meter-heading">
				<strong>{label}</strong>
				<span>{metric.usedPercent.toFixed(1)}% used</span>
			</div>
			<div
				aria-label={`${label}: ${metric.usedPercent.toFixed(1)}% used`}
				aria-valuemax={100}
				aria-valuemin={0}
				aria-valuenow={metric.usedPercent}
				className="owner-meter-track"
				role="meter"
			>
				<span style={{ width: `${Math.min(100, Math.max(0, metric.usedPercent))}%` }} />
			</div>
			<div className="owner-meter-detail">
				<span>{formatBytes(metric.usedBytes)} used</span>
				<span>{formatBytes(metric.freeBytes)} free</span>
			</div>
		</article>
	);
}

export function PlatformDashboard() {
	const [dashboard, setDashboard] = useState<PlatformDashboardSummary>();
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string>();
	const requestInFlight = useRef(false);

	const load = useCallback(async (background = false) => {
		if (requestInFlight.current) return;
		requestInFlight.current = true;
		if (background) setRefreshing(true);
		try {
			setDashboard(await getPlatformDashboard());
			setError(undefined);
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not load the owner dashboard',
			);
		} finally {
			setLoading(false);
			setRefreshing(false);
			requestInFlight.current = false;
		}
	}, []);

	useEffect(() => {
		void load();
		const interval = window.setInterval(() => void load(), DASHBOARD_REFRESH_MS);
		return () => window.clearInterval(interval);
	}, [load]);

	if (loading && !dashboard) {
		return (
			<section className="workspace-section owner-dashboard">
				<div className="empty-configuration">Loading platform dashboard…</div>
			</section>
		);
	}

	if (!dashboard) {
		return (
			<section className="workspace-section owner-dashboard">
				<div className="alert error">{error ?? 'Could not load the owner dashboard'}</div>
				<button className="secondary-button" onClick={() => void load()}>
					Try again
				</button>
			</section>
		);
	}

	const { overview, server } = dashboard;

	return (
		<section className="workspace-section owner-dashboard">
			<div className="section-heading">
				<div>
					<p className="eyebrow">Platform administration</p>
					<h2>Dashboard</h2>
					<p className="muted">Customers, activity and server capacity in one place.</p>
				</div>
				<button
					className="secondary-button"
					disabled={refreshing}
					onClick={() => void load(true)}
				>
					{refreshing ? 'Refreshing…' : 'Refresh'}
				</button>
			</div>

			{error && <div className="alert error">Latest refresh failed: {error}</div>}

			<div className="owner-overview-grid">
				<article className="owner-overview-card">
					<span>Customers</span>
					<strong>{overview.customers.toLocaleString()}</strong>
					<small>{overview.activeCustomers.toLocaleString()} active</small>
				</article>
				<article className="owner-overview-card">
					<span>People</span>
					<strong>{overview.humanUsers.toLocaleString()}</strong>
					<small>{overview.activeMemberships.toLocaleString()} active memberships</small>
				</article>
				<article className="owner-overview-card">
					<span>AI Agents</span>
					<strong>{overview.aiAgents.toLocaleString()}</strong>
					<small>Across all customers</small>
				</article>
				<article className="owner-overview-card">
					<span>Active chats</span>
					<strong>{overview.activeConversations.toLocaleString()}</strong>
					<small>
						{overview.conversationsToday.toLocaleString()} chats ·{' '}
						{overview.messagesToday.toLocaleString()} messages today
					</small>
				</article>
			</div>

			<section className="owner-dashboard-panel">
				<div className="owner-panel-heading">
					<div>
						<h3>Server</h3>
						<p>Live capacity and software information from this application server.</p>
					</div>
					<small>Updated {new Date(dashboard.generatedAt).toLocaleTimeString()}</small>
				</div>
				<div className="owner-server-grid">
					<article className="owner-meter-card">
						<div className="owner-meter-heading">
							<strong>CPU utilisation</strong>
							<span>
								{server.cpu.utilizationPercent.toFixed(1)}% across{' '}
								{server.cpu.cores} cores
							</span>
						</div>
						<div
							aria-label={`CPU utilisation: ${server.cpu.utilizationPercent.toFixed(1)}%`}
							aria-valuemax={100}
							aria-valuemin={0}
							aria-valuenow={server.cpu.utilizationPercent}
							className="owner-meter-track"
							role="meter"
						>
							<span style={{ width: `${server.cpu.utilizationPercent}%` }} />
						</div>
						<div className="owner-meter-detail">
							<span>1-minute load {server.cpu.load1.toFixed(2)}</span>
							<span>5-minute load {server.cpu.load5.toFixed(2)}</span>
							<span>15-minute load {server.cpu.load15.toFixed(2)}</span>
						</div>
					</article>
					<CapacityCard label="RAM" metric={server.memory} />
					<CapacityCard label="Disk" metric={server.disk} />
				</div>

				<div className="owner-software-grid">
					<article>
						<span>Livechat</span>
						<strong>v{server.appVersion}</strong>
						<small>
							{server.livechatCommit
								? `Commit ${server.livechatCommit.slice(0, 10)}`
								: 'Commit unavailable'}
							{server.deployedAt
								? ` · deployed ${new Date(server.deployedAt).toLocaleString()}`
								: ''}
						</small>
					</article>
					<article>
						<span>PostgreSQL</span>
						<strong>{server.postgresql.version}</strong>
						<small>
							{formatBytes(server.postgresql.databaseBytes)} ·{' '}
							{server.postgresql.connections} connections
						</small>
					</article>
					<article>
						<span>Redis</span>
						<strong>{server.redis.version}</strong>
						<small>
							{formatBytes(server.redis.usedMemoryBytes)} ·{' '}
							{server.redis.connectedClients} clients
						</small>
					</article>
					<article>
						<span>Runtime</span>
						<strong>{server.nodeVersion}</strong>
						<small>
							{server.platform}
							<br />
							App uptime {formatDuration(server.appUptimeSeconds)} · Server uptime{' '}
							{formatDuration(server.serverUptimeSeconds)}
						</small>
					</article>
				</div>
			</section>
		</section>
	);
}
